import { createColumnHelper } from "@tanstack/react-table";
import { useCreateNotification, useDeleteNotification, useGetNotificationById, useGetNotifications, useGetNotificationTypes, useUpdateNotifications } from "../../../queries/notifications";
import { useMemo, useState, useEffect } from "react";
import TanStackTable from "../../../components/TanStackTable";
import { ActionButton } from "../../../components/Forms/ActionButton";
import Modal from "../../Submissions/components/Modal";
import 'react-toastify/dist/ReactToastify.css';
import { toast, ToastContainer } from "react-toastify";
import ActiveStatus from "../../../components/Common/ActiveStatus";
import { FaEye, FaTrash } from "react-icons/fa";
import { useForm } from "react-hook-form";
import NewDropdown from "../../../components/Forms/NewDropdown";
import { getAdminType } from "../../../utils/adminType";

const Notifications = () => {

    const adminType = getAdminType();
    
    const { data, isPending } = useGetNotifications();
    const { data: notificationTypes, isLoading } = useGetNotificationTypes();

    const [showCreateModal, setShowCreateModal] = useState(false);
    const [selectedNotification, setSelectedNotification] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        title: '',
        fromDate: '',
        toDate: '',
        status: 'all',
        notificationType: ''
    });

    // Applied filters state - only updated when search button is clicked
    const [appliedFilters, setAppliedFilters] = useState({
        title: '',
        fromDate: '',
        toDate: '',
        status: 'all',
        notificationType: ''
    });

    const { mutateAsync: updateNotification, isPending: isUpdatingNotification } = useUpdateNotifications();
    const { mutateAsync: deleteNotification, isPending: isDeletingNotification } = useDeleteNotification();
    const { mutateAsync: createNotification, isPending: isCreatingNotification } = useCreateNotification();

    // Filter and search functionality
    const filteredData = useMemo(() => {
        if (!data) return [];

        return data.filter(notification => {
            // Title filter
            if (appliedFilters.title && !notification.title?.toLowerCase().includes(appliedFilters.title.toLowerCase())) {
                return false;
            }

            // Date range filter
            const notificationDate = new Date(notification.createdAt);
            if (appliedFilters.fromDate) {
                const fromDate = new Date(appliedFilters.fromDate);
                fromDate.setHours(0, 0, 0, 0);
                if (notificationDate < fromDate) return false;
            }
            if (appliedFilters.toDate) {
                const toDate = new Date(appliedFilters.toDate);
                toDate.setHours(23, 59, 59, 999);
                if (notificationDate > toDate) return false;
            }

            // Status filter
            if (appliedFilters.status !== 'all') {
                const isActive = appliedFilters.status === 'active';
                if (notification.active !== isActive) return false;
            }

            // Notification type filter
            if (appliedFilters.notificationType && notification.types?._id !== appliedFilters.notificationType) {
                return false;
            }

            return true;
        });
    }, [data, appliedFilters]);

    // Handle search functionality
    const handleSearch = () => {
        // Apply current filters when search button is clicked
        setAppliedFilters({
            title: filters.title,
            fromDate: filters.fromDate,
            toDate: filters.toDate,
            status: filters.status,
            notificationType: filters.notificationType
        });
    };

    // Reset filters
    const handleReset = () => {
        const resetState = {
            title: '',
            fromDate: '',
            toDate: '',
            status: 'all',
            notificationType: ''
        };
        setFilters(resetState);
        setAppliedFilters(resetState);
    };

    const handleNotificationStatusToggle = async (notificationId, currentStatus) => {
        await updateNotification(
            { id: notificationId, active: !currentStatus },
            {
                onSuccess: () => {
                    toast.success(`Notification status updated successfully`);
                },
                onError: (error) => {
                    toast.error("Failed to update notification status: " + (error.message || "Unknown error"));
                }
            }
        );
    };

    const handleDelete = async (id) => {
        const confirm = window.confirm("Are you sure you want to delete this notification?");
        if (!confirm) return;

        await deleteNotification(id, {
            onSuccess: () => {
                toast.success("Notification deleted successfully");
            },
            onError: (error) => {
                toast.error("Failed to delete notification: " + (error.message || "Unknown error"));
            }
        });
    };

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue,
        watch,
    } = useForm({
        defaultValues: {
            types: '',
            title: '',
            message: '',
            isInAppOnly: false,
            isPushOnly: false,
            isEmailOnly: false,
            active: true, // Default to active
        },
    });

    const watchTypes = watch("types");

    const onSubmit = async (data) => {
        // Validate notification type
        console.log(!data.types);
        if (!data.types) {
            toast.error("Please select a notification type");
            return;
        }

        const submitData = {
            ...data,
            types: data.types.value || data.types,
        };

        if (isEditMode && selectedNotification) {
            // Update existing notification
            await updateNotification({
                id: selectedNotification,
                ...submitData
            }, {
                onSuccess: (res) => {
                    if (res.error) {
                        toast.error("Failed to update notification");
                        return;
                    }
                    toast.success("Notification updated successfully");
                    setShowCreateModal(false);
                    setIsEditMode(false);
                    setSelectedNotification(null);
                    reset();
                },
                onError: (error) => {
                    toast.error("Failed to update notification: " + (error.message || "Unknown error"));
                }
            });
        } else {
            // Create new notification
            await createNotification(submitData, {
                onSuccess: (res) => {
                    if (res.error) {
                        toast.error("Failed to create notification");
                        return;
                    }
                    toast.success("Notification created successfully");
                    setShowCreateModal(false);
                    reset();
                },
                onError: (error) => {
                    toast.error("Failed to create notification: " + (error.message || "Unknown error"));
                }
            });
        }
    }

    const { data: notification, refetch: refetchNotification } = useGetNotificationById(selectedNotification);

    // Handle closing modal with cleanup
    const handleCloseModal = () => {
        setShowCreateModal(false);
        setIsEditMode(false);
        setSelectedNotification(null);
        reset();
    };

    // Handle opening create modal
    const handleOpenCreateModal = () => {
        reset();
        setIsEditMode(false);
        setSelectedNotification(null);
        setShowCreateModal(true);
    };

    // Handle opening edit modal
    const handleOpenNotificationModal = (notificationId) => {
        setSelectedNotification(notificationId);
        setIsEditMode(true);
        setShowCreateModal(true);
    };

    // Effect to populate form fields when notification data is loaded for editing
    useEffect(() => {
        if (notification && isEditMode && selectedNotification && notificationTypes?.length > 0) {
            // Find the notification type option
            const notificationTypeOption = notificationTypes.find(type => type._id === notification.types?._id);
            if (notificationTypeOption) {
                setValue("types", notificationTypeOption._id);
            } else {
                console.warn('Notification type not found for:', notification.types);
                setValue("types", '');
            }
            setValue("title", notification.title || '');
            setValue("message", notification.message || '');
            setValue("active", notification.active || true);
            setValue("isInAppOnly", notification.isInAppOnly || false);
            setValue("isPushOnly", notification.isPushOnly || false);
            setValue("isEmailOnly", notification.isEmailOnly || false);
        }
    }, [notification, isEditMode, selectedNotification, notificationTypes, setValue]);

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('types.types', {
            header: 'Type',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('title', {
            header: 'Title',
            cell: info => info.getValue() || 'N/A',
        }),
        columnHelper.accessor('message', {
            header: 'Message',
            cell: info => (
                <div className="max-w-xs overflow-hidden text-ellipsis whitespace-nowrap">
                    {info.getValue() || 'N/A'}
                </div>
            ),
        }),
        columnHelper.accessor('createdAt', {
            header: 'Created At',
            align: 'center',
            cell: info => (
                <div className='text-center'>
                    {new Date(info.getValue()).toLocaleDateString('en-IN', { month: 'short', day: 'numeric', year: 'numeric' })}
                    <br />
                    <small className='text-secondary'>
                        {new Date(info.getValue()).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                    </small>
                </div>
            ),
        }),
        columnHelper.accessor('actions', {
            header: 'Actions',
            align: 'center',
            enableColumnFilter: false,
            cell: info => {
                const review = info.row.original;
                return (
                    <div className='flex items-center justify-center gap-2'>
                        <div className='min-w-fit'>
                            <ActiveStatus
                                active={review.active}
                                setActive={() => handleNotificationStatusToggle(review._id, review.active)}
                                label=""
                                innerActiveText="Show"
                                innerInactiveText="Hide"
                                hideLabel={true}
                            />
                        </div>
                        <button
                            className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                            onClick={() => handleOpenNotificationModal(review._id)}
                            title="View Request"
                        >
                            <FaEye className="fill-primary" />
                        </button>
                        {
                            adminType === 'superadmin' && (
                                <button
                                    className="rounded p-1.5 bg-[rgba(199,52,52,0.1)] hover:bg-[rgba(199,52,52,0.2)] mx-1 min-h-7"
                                    onClick={() => handleDelete(review._id)}
                                    title="Delete Notification"
                                    disabled={isDeletingNotification}
                                >
                                    <FaTrash className="fill-chipRed" />
                                </button>
                            )
                        }
                    </div>
                )
            },
        }),
    ], [updateNotification, deleteNotification]);

    if (isPending || isLoading) {
        return <div>Loading notifications...</div>;
    }

    return (
        <main className="flex flex-col text-base font-semibold text-gray-900 rounded-none">

            <Modal isOpen={showCreateModal} onClose={handleCloseModal} title={isEditMode ? "Edit Notification" : "Add Notification"}>
                <div className="flex flex-col items-center justify-center h-full">
                    <div className="w-full">
                        <h2 className="text-lg font-bold mb-4">{isEditMode ? "Edit Notification" : "Add Notification"}</h2>
                        <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
                            <div className="flex flex-col gap-0">
                                <span>Notification Type</span>
                                <NewDropdown
                                    label="Notification Type"
                                    placeholder="Select Notification Type"
                                    dataList={notificationTypes?.map(item => ({ value: item._id, label: item.types })) || []}
                                    name={"types"}
                                    disabled={isEditMode}
                                    value={watchTypes}
                                    onChange={(value) => {
                                        setValue("types", value);
                                    }}
                                />
                                {errors.types && <span className="text-red">{errors.types.message}</span>}
                            </div>
                            <div className="flex gap-5 items-center justify-start">
                                <ActiveStatus
                                    active={watch("isInAppOnly") || false}
                                    setActive={(value) => setValue("isInAppOnly", value)}
                                    label="In App"
                                    innerActiveText="Yes"
                                    innerInactiveText="No"
                                />
                                <ActiveStatus
                                    active={watch("isPushOnly") || false}
                                    setActive={(value) => setValue("isPushOnly", value)}
                                    label="Push"
                                    innerActiveText="Yes"
                                    innerInactiveText="No"
                                />
                                <ActiveStatus
                                    active={watch("isEmailOnly") || false}
                                    setActive={(value) => setValue("isEmailOnly", value)}
                                    label="Email"
                                    innerActiveText="Yes"
                                    innerInactiveText="No"
                                />
                            </div>
                            <div className="flex flex-col gap-0">
                                <span>Title</span>
                                <input
                                    type="text"
                                    placeholder="Enter notification title"
                                    className="border border-navBtn rounded-md p-2"
                                    {...register("title", {
                                        required: "Notification title is required",
                                    })}
                                />
                                {errors.title && <span className="text-red">{errors.title.message}</span>}
                            </div>
                            <div className="flex flex-col gap-0">
                                <span>Message</span>
                                <textarea
                                    placeholder="Enter notification message"
                                    className="border border-navBtn rounded-md p-2 resize-none"
                                    {...register("message", {
                                        required: "Notification message is required",
                                    })}
                                    rows={5}
                                />
                                {errors.message && <span className="text-red">{errors.message.message}</span>}
                            </div>
                            <div className="flex justify-end gap-4 mt-4">
                                <ActionButton
                                    isSubmit={true}
                                    text="Save"
                                    isLoading={isUpdatingNotification || isCreatingNotification}
                                    onClick={handleSubmit(onSubmit)}
                                />
                            </div>
                        </form>
                    </div>
                </div>
            </Modal>

            {/* Filters */}
            <form
                className="flex flex-col pt-5 w-full bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)]"
                onSubmit={(e) => {
                    e.preventDefault();
                    handleSearch();
                }}
            >
                <div className='flex items-center justify-between pr-5'>
                    <div className="flex gap-1.5 items-start my-auto text-base whitespace-nowrap ml-5">
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/4dd39feef0ff4934d9e2e86b8c7285d5d7532aeb26adcdc1e878d021439e2681?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 w-5 aspect-square"
                        />
                        <span className="self-stretch">Filters</span>
                        <img
                            loading="lazy"
                            src="https://cdn.builder.io/api/v1/image/assets/TEMP/d18f51a0b2d597a302f55b77fc1071043eab5f274a1cc9e9b3fe3e2c97520a8c?placeholderIfAbsent=true&apiKey=d62bea31e5c5472d8bb4326e92b00478"
                            alt=""
                            className="object-contain shrink-0 aspect-square w-[18px]"
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-4 p-5">
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="status" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Status
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <select
                                    name="status"
                                    value={filters.status}
                                    onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                                    className="flex-1 shrink self-stretch my-auto basis-0 bg-transparent border-none outline-none text-base"
                                >
                                    <option value="all">All</option>
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive</option>
                                </select>
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="notificationType" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Notification Type
                            </label>
                            <div className="mt-2">
                                <NewDropdown
                                    placeholder="Select Notification Type"
                                    dataList={notificationTypes?.map(item => ({ value: item._id, label: item.types })) || []}
                                    name="notificationType"
                                    value={filters.notificationType}
                                    onChange={(value) => {
                                        setFilters(prev => ({ ...prev, notificationType: value.value || '' }));
                                    }}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="fromDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                From Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="fromDate"
                                    value={filters.fromDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, fromDate: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="flex-col w-full">
                            <label htmlFor="toDate" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                To Date
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="date"
                                    name="toDate"
                                    value={filters.toDate}
                                    onChange={(e) => setFilters(prev => ({ ...prev, toDate: e.target.value }))}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='flex gap-3 w-full'>
                        <div className="flex-col w-full">
                            <label htmlFor="title" className="gap-1 self-stretch w-full text-gray-900 text-base">
                                Title
                            </label>
                            <div className="flex gap-2.5 items-center px-4 py-2.5 mt-2.5 w-full whitespace-nowrap bg-white rounded-md border border-solid border-navBtn text-slate-400">
                                <input
                                    type="text"
                                    name="title"
                                    value={filters.title}
                                    onChange={(e) => setFilters(prev => ({ ...prev, title: e.target.value }))}
                                    placeholder="Search by title..."
                                    className='outline-none focus:ring-0 w-full'
                                />
                            </div>
                        </div>
                    </div>
                </div>
                <div className='flex gap-3 items-center justify-end p-5'>
                    <ActionButton
                        variant={'reset'}
                        text={'Reset'}
                        handleSubmit={handleReset}
                    />
                    <ActionButton
                        variant={'submit'}
                        text={'Search'}
                        handleSubmit={() => { }}
                    />
                </div>
            </form>

            <section className="flex flex-col pt-5 w-full mt-5 bg-white rounded-md shadow-[0px_0px_25px_rgba(94,92,154,0.1)] max-md:max-w-full">
                <div className="flex flex-col pr-px w-full pl-6 max-md:max-w-full">
                    <div className="flex items-center justify-between pb-4">
                        <h1 className="self-start text-lg font-bold">List of Notifications</h1>
                        {
                            adminType === 'superadmin' && (
                                <ActionButton
                                    text="+ Add New Notification"
                                    handleSubmit={handleOpenCreateModal}
                                    variant="submit"
                                />
                            )
                        }
                    </div>
                    {filteredData && filteredData.length > 0 ? (
                        <TanStackTable
                            data={filteredData}
                            columns={columns}
                            enablePagination={true}
                        />
                    ) : (
                        <div className="pb-4">
                            No Notifications available
                        </div>
                    )}
                </div>
            </section>
            <ToastContainer
                position="top-right"
                autoClose={2000}
            />
        </main>
    );
}

export default Notifications;